'use client';
import { authGeneration } from './token';
/** Tiny cross-component channel to open a specific brain conversation from anywhere (the conversation
 *  switcher, a pinned sidebar row, the dashboard recap). A request lands on the full /chat page; only one
 *  that asks for the dock (`dock: true`, the advisor launcher and the controls inside the dock itself) opens
 *  the side panel. Two consumers must cooperate across a mount gap: the Shell routes to the chat host, and
 *  the single BrainChatProvider then loads the requested conversation. A module-level `pending` bridges
 *  that gap — the provider consumes it on its first attach AND handles the live event when it is already
 *  attached. `continuable` = the session can be resumed and continued (own web/CLI conversation);
 *  otherwise it opens read-only (a shared Discord channel or a task-worker session). */
export const BRAIN_OPEN_EVENT = 'elowen:open-brain-session';
/** Opens the normal live conversation and optionally seeds its composer. Quick-action suggestions
 *  and the persistent Elowen launcher use this; the dashboard send uses startNewConversation instead. */
export const BRAIN_COMPOSE_EVENT = 'elowen:open-brain-composer';

/** Where a request lands: carried only when true, the side dock; otherwise the full /chat page. The
 *  advisor launcher asks for the dock, and so do the dock's own controls, which act on the conversation
 *  already shown there. Explicit dock requests use a fullscreen surface on narrow viewports. */
interface BrainChatTarget { dock?: boolean; /** The provider already bound this request; only its placement remains. */ displayOnly?: boolean }

export interface BrainOpenRequest extends BrainChatTarget { sessionId: string; continuable: boolean; /** The request names a DELEGATED sub-agent transcript: it always opens read-only, never bound as the account's active conversation. */ delegated?: boolean; /** The name of the conversation the transcript belongs to, as the opener lists it: the row's own title, or a delegated child's parent conversation title. A read-only view shows it in the conversation bar when the account's own listing does not hold the session (another account's conversation, a sub-agent). Carried only when nonempty. */ title?: string; /** A delegated child's own name, as the opener lists it (the register's sub-agent node name). The conversation bar shows it beside `title`. Carried only when nonempty. */ name?: string }

/** The live compose event's detail: the text to seed (empty = just focus) and where it lands. */
export interface BrainComposeRequest extends BrainChatTarget { text: string }

type ConversationDockOpener = (options: { sessionId: string; continuable: boolean; title?: string }) => Promise<void>;
let conversationDockOpener: ConversationDockOpener | null = null;
let dockRequestGeneration = 0;

/** The provider binds the requested conversation before the shell displays its surface. */
export function registerBrainConversationDockOpener(open: ConversationDockOpener): () => void {
  conversationDockOpener = open;
  return () => { if (conversationDockOpener === open) conversationDockOpener = null; };
}

export async function openBrainConversationDock(options: { sessionId: string; continuable: boolean; title?: string }): Promise<void> {
  if (!options.sessionId) throw new Error('A conversation session is required.');
  if (!conversationDockOpener) throw new Error('Conversation provider is not mounted.');
  const generation = ++dockRequestGeneration;
  const issuedIn = authGeneration();
  const opener = conversationDockOpener;
  await opener(options);
  if (generation !== dockRequestGeneration || issuedIn !== authGeneration() || opener !== conversationDockOpener) throw new Error('Conversation selection was superseded.');
  window.dispatchEvent(new CustomEvent(BRAIN_OPEN_EVENT, { detail: { ...options, dock: true, displayOnly: true } }));
}

type ConversationStarter = (options: { draft: string }) => Promise<string>;
let conversationStarter: ConversationStarter | null = null;

/** The mounted provider owns creation and draft state; the runtime never creates a second controller. */
export function registerBrainConversationStarter(start: ConversationStarter): () => void {
  conversationStarter = start;
  return () => { conversationStarter = null; };
}

export async function startBrainConversation(options: { draft: string }): Promise<string> {
  if (!conversationStarter) throw new Error('Conversation provider is not mounted.');
  return conversationStarter(options);
}

let pending: BrainOpenRequest | null = null;
let pendingComposer: string | null = null;

/** Preserve both drafts when a suggestion/launcher compose request reaches an already-edited chat. */
export function mergeBrainComposerText(current: string, incoming: string): string {
  if (!incoming) return current;
  if (!current || current === incoming) return incoming;
  return `${current}\n\n${incoming}`;
}

/** Request the given stored session — continue it, focus it as a delegated child read-only, or view an
 *  external transcript read-only — on the /chat page, or in the dock when `dock` asks for it. `delegated`
 *  and `dock` are carried only when true and `title`/`name` only when nonempty, so a request without them
 *  keeps its two-field shape. */
export function openBrainSession(sessionId: string, continuable: boolean, { delegated = false, title = '', name = '', dock = false }: { delegated?: boolean; title?: string; name?: string; dock?: boolean } = {}): void {
  request({ sessionId, continuable, ...(delegated ? { delegated: true } : {}), ...(title ? { title } : {}), ...(name ? { name } : {}), ...(dock ? { dock: true } : {}) });
}

function request(next: BrainOpenRequest): void {
  pending = next;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BRAIN_OPEN_EVENT, { detail: pending }));
  }
}

/** Canonical URL for opening one stored conversation in its own full chat window. */
export function brainSessionUrl(sessionId: string): string {
  return `/chat?${new URLSearchParams({ session: sessionId }).toString()}`;
}

/** Open a canonical chat session in a new, isolated browser tab. */
export function openBrainSessionWindow(sessionId: string): void {
  if (typeof window !== 'undefined') window.open(brainSessionUrl(sessionId), '_blank', 'noopener,noreferrer');
}

/** Read and clear the pending request (BrainChat calls this once on mount / when handling the event). */
export function consumePendingBrainSession(): BrainOpenRequest | null {
  const req = pending;
  pending = null;
  return req;
}

/** Open the live conversation with `text` ready to edit/send, on the /chat page or, when `dock` asks for
 *  it, in the dock. An empty string simply focuses the composer. The module-level bridge survives the
 *  chat host's mount gap, like session requests above. */
export function openBrainComposer(text = '', { dock = false }: { dock?: boolean } = {}): void {
  pendingComposer = mergeBrainComposerText(pendingComposer ?? '', text);
  if (typeof window !== 'undefined') {
    const detail: BrainComposeRequest = { text, ...(dock ? { dock: true } : {}) };
    window.dispatchEvent(new CustomEvent(BRAIN_COMPOSE_EVENT, { detail }));
  }
}

/** BrainChat consumes the pending composer value on mount or when it handles a live compose event. */
export function consumePendingBrainComposer(): string | null {
  const text = pendingComposer;
  pendingComposer = null;
  return text;
}

/** Where a request for the dock should land.
 *
 *  A phone gets the full /chat page rather than the dock: the dock is a side panel sized in PIXELS and
 *  anchored to a window edge, with a drag handle and a resizable split, so on a phone it arrived as a
 *  cramped overlay with the conversation squeezed into whatever width was left over.
 *
 *  `mobile` is `undefined` until the viewport has actually been measured, and that case deliberately
 *  resolves to the dock: the measurement lands on mount, long before anyone can tap, and guessing
 *  "mobile" would send a desktop user to a different page for one frame. */
export function advisorOpenTarget(opts: { onChat: boolean; mobile: boolean | undefined }): 'none' | 'chat-page' | 'dock' {
  if (opts.onChat) return 'none'; // that page is already the chat host; popping the dock would duplicate it
  return opts.mobile === true ? 'chat-page' : 'dock';
}
